import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { TrendingUp, ArrowRight, Globe } from "lucide-react";
import { PageShell, Eyebrow, Ticker } from "../components/Layout";
import { MONEY_METRICS, CORRIDORS } from "../data/mock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FX_LABELS = { USD: "US Dollar", EUR: "Euro", GBP: "British Pound", NZD: "NZ Dollar", JPY: "Japanese Yen", INR: "Indian Rupee", SGD: "Singapore Dollar" };

const MoneyFlow = () => {
  const [cashRate, setCashRate] = useState(null);
  const [fx, setFx] = useState(null);

  useEffect(() => {
    axios.get(`${API}/market/cash-rate`).then((r) => setCashRate(r.data)).catch(() => {});
    axios.get(`${API}/market/fx`).then((r) => setFx(r.data)).catch(() => {});
  }, []);

  return (
  <PageShell>
    <section className="hero-grid border-b border-neutral-200">
      <div className="max-w-[1320px] mx-auto px-5 lg:px-8 pt-16 pb-14">
        <Eyebrow>Money Flow Intelligence</Eyebrow>
        <h1 className="font-heading text-5xl lg:text-[60px] font-bold tracking-tight text-ink leading-[1.02]">Live signals —<br />straight from the corridor.</h1>
        <p className="mt-6 text-[16px] text-ink/60 max-w-2xl leading-relaxed">RBA policy, the AUD trade-weighted index, NPP · PayID volumes and the remittance corridors that matter to Australian payments and AML teams.</p>
      </div>
    </section>
    <Ticker />
    <section className="py-16">
      <div className="max-w-[1320px] mx-auto px-5 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-10">
          <div data-testid="money-metric-0" className="border border-neutral-200 rounded-lg p-7 bg-white hover:shadow-[0_16px_40px_rgba(15,27,45,0.07)] transition-shadow duration-200">
            <div className="flex items-center justify-between">
              <p className="font-mono text-[11px] tracking-[0.2em] uppercase text-neutral-400">{MONEY_METRICS[0].label}</p>
              <span className={`font-mono text-[10px] tracking-widest uppercase px-2 py-0.5 rounded-sm border ${cashRate?.live ? "text-emerald-600 border-emerald-200 bg-emerald-50" : "text-neutral-400 border-neutral-200"}`}>
                {cashRate?.live ? "Live" : "Indicative"}
              </span>
            </div>
            <p className="font-heading text-5xl font-bold text-ink mt-4" data-testid="cash-rate-value">{cashRate ? `${cashRate.rate.toFixed(2)}%` : "—"}</p>
            <p className="font-mono text-[12px] text-emerald-600 mt-2">{cashRate?.date ? `As at ${cashRate.date}` : ""}</p>
            <p className="text-[13px] text-ink/55 leading-relaxed mt-4">{cashRate?.source ? `Source · ${cashRate.source}` : MONEY_METRICS[0].note}</p>
          </div>
          {MONEY_METRICS.slice(1).map((m, i) => (
            <div key={m.label} data-testid={`money-metric-${i + 1}`} className="border border-neutral-200 rounded-lg p-7 bg-white hover:shadow-[0_16px_40px_rgba(15,27,45,0.07)] transition-shadow duration-200">
              <div className="flex items-center justify-between">
                <p className="font-mono text-[11px] tracking-[0.2em] uppercase text-neutral-400">{m.label}</p>
                <span className="font-mono text-[10px] tracking-widest uppercase px-2 py-0.5 rounded-sm border text-neutral-400 border-neutral-200">Indicative</span>
              </div>
              <p className="font-heading text-5xl font-bold text-ink mt-4">{m.value}</p>
              <p className="font-mono text-[12px] text-emerald-600 mt-2">{m.sub}</p>
              <p className="text-[13px] text-ink/55 leading-relaxed mt-4">{m.note}</p>
            </div>
          ))}
        </div>

        {/* Live FX panel */}
        <div className="border border-neutral-200 rounded-lg overflow-hidden mb-10" data-testid="fx-panel">
          <div className="bg-ink text-white px-7 py-4 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <Globe size={16} className="text-accent-light" />
              <h2 className="font-heading text-lg font-bold">AUD exchange rates</h2>
            </div>
            <span className="font-mono text-[10.5px] text-white/40 tracking-wider hidden sm:block">
              {fx?.live ? `LIVE · ${fx.source?.toUpperCase() || "ECB"} · ${fx.date || ""}` : "INDICATIVE"}
            </span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 divide-x divide-y lg:divide-y-0 divide-neutral-100">
            {fx ? Object.entries(fx.rates).map(([cur, rate]) => (
              <div key={cur} className="p-5 hover:bg-paper transition-colors duration-150" data-testid={`fx-${cur}`}>
                <p className="font-mono text-[11px] tracking-[0.15em] text-accent">AUD / {cur}</p>
                <p className="font-heading text-2xl font-bold text-ink mt-2">{Number(rate).toFixed(cur === "JPY" || cur === "INR" ? 2 : 4)}</p>
                <p className="text-[11.5px] text-ink/45 mt-1">{FX_LABELS[cur] || cur}</p>
              </div>
            )) : (
              <div className="p-6 col-span-full text-sm text-neutral-400">Loading live rates…</div>
            )}
          </div>
        </div>
        <div className="border border-neutral-200 rounded-lg overflow-hidden">
          <div className="bg-ink text-white px-7 py-4 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <TrendingUp size={16} className="text-accent-light" />
              <h2 className="font-heading text-lg font-bold">Top corridors · AUD mn monthly</h2>
            </div>
            <span className="font-mono text-[10.5px] text-white/40 tracking-wider hidden sm:block">INDICATIVE · COMPOSITE INDEX</span>
          </div>
          {CORRIDORS.map((c, i) => (
            <div key={c.name} data-testid={`corridor-row-${i}`} className="grid grid-cols-12 items-center px-7 py-5 border-b border-neutral-100 last:border-0 hover:bg-paper transition-colors duration-150">
              <div className="col-span-7 sm:col-span-6 flex items-center gap-3 min-w-0">
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse shrink-0" />
                <span className="text-[14px] text-ink/85 font-medium truncate">{c.name}</span>
              </div>
              <div className="col-span-2 hidden sm:block">
                <span className="font-mono text-[10px] tracking-widest uppercase text-emerald-600 border border-emerald-200 bg-emerald-50 px-2 py-0.5 rounded-sm">Live</span>
              </div>
              <div className="col-span-3 sm:col-span-2 text-right font-heading text-lg font-bold text-ink">{c.vol}</div>
              <div className="col-span-2 text-right font-mono text-[12px] text-emerald-600">{c.yoy}</div>
            </div>
          ))}
        </div>
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div className="border-l-[3px] border-accent bg-paper rounded-r-lg p-7">
            <h3 className="font-mono text-[11px] tracking-[0.25em] uppercase text-accent mb-3">Why this matters</h3>
            <p className="text-[14.5px] text-ink/70 leading-[1.8]">Corridor concentration is an AML/CTF risk signal. AUSTRAC's Tranche 2 expansion will pull remittance-adjacent professionals into the reporting net from July 2026 — entities exposed to high-growth corridors should refresh their ML/TF risk assessments now.</p>
          </div>
          <div className="bg-ink text-white rounded-lg p-7 flex flex-col justify-between">
            <div>
              <h3 className="font-heading text-xl font-bold">Weekly Money Flow briefing</h3>
              <p className="text-[13.5px] text-white/55 mt-2 leading-relaxed">RBA decisions, corridor moves and payments-policy signals — in your inbox every Monday.</p>
            </div>
            <Link to="/#newsletter" onClick={() => setTimeout(() => window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" }), 100)} className="inline-flex items-center gap-1.5 text-accent-light text-[13.5px] font-semibold mt-5 hover:gap-2.5 transition-[gap] duration-200">Subscribe free <ArrowRight size={14} /></Link>
          </div>
        </div>
      </div>
    </section>
  </PageShell>
  );
};

export default MoneyFlow;
